import { useEffect, useState } from 'react'
import { fetchBlob, recordingExtension, saveBlob } from '@/api/client'

interface RecordingPlayerProps {
  sessionId: string
  candidateName?: string | null
}

/**
 * Inline playback + download for a session recording. The artifact endpoint is
 * authenticated, so the audio loads as a blob — a plain <audio src> cannot
 * carry the bearer token. A missing recording (404) renders nothing: not every
 * session has audio, and absence is not an error. Every other load failure
 * (auth, artifact store, network) surfaces inline rather than masquerading as
 * "no recording", so the candidate knows to retry.
 */
export function RecordingPlayer({ sessionId, candidateName }: RecordingPlayerProps) {
  const [audioUrl, setAudioUrl] = useState<string | null>(null)
  // The loaded blob itself: the download saves these bytes instead of
  // re-fetching them from the artifact endpoint.
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null)
  const [audioExt, setAudioExt] = useState('webm')
  const [loadError, setLoadError] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const [downloadError, setDownloadError] = useState(false)

  useEffect(() => {
    // React Router reuses this element across same-route param changes
    // (/my-interviews/:id, /admin/session/:id), so the previous session's
    // state must clear before the new fetch starts: its blob URL was revoked
    // by the cleanup below, and its load failure's early return would mask a
    // session that does have a recording.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setAudioBlob(null)
    setAudioUrl(null)
    setAudioExt('webm')
    setLoadError(false)
    setDownloadError(false)
    let objectUrl: string | null = null
    let cancelled = false
    fetchBlob(`/download/${sessionId}/audio`)
      .then((blob: Blob) => {
        if (cancelled) return
        objectUrl = URL.createObjectURL(blob)
        setAudioBlob(blob)
        setAudioUrl(objectUrl)
        setAudioExt(recordingExtension(blob))
      })
      .catch(err => {
        if (cancelled) return
        if (err?.response?.status === 404) return
        console.error('Failed to load the interview recording:', {
          sessionId,
          status: err?.response?.status,
          message: err?.message,
        })
        setLoadError(true)
      })
    return () => {
      cancelled = true
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [sessionId])

  if (loadError) {
    return (
      <span className="hint" role="alert">
        Could not load the recording. Refresh the page to try again.
      </span>
    )
  }
  if (!audioUrl) return null

  const download = async () => {
    if (!audioBlob) return
    setDownloading(true)
    setDownloadError(false)
    try {
      await saveBlob(audioBlob, `interview-recording-${candidateName || 'candidate'}.${audioExt}`)
    } catch (err) {
      // The button encourages retrying, so a silent failure would loop the
      // user into the same wall — leave a trace of the failure shape.
      console.error('Failed to download the interview recording:', {
        sessionId,
        message: err instanceof Error ? err.message : undefined,
      })
      setDownloadError(true)
    } finally {
      setDownloading(false)
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <audio controls src={audioUrl} className="h-9" aria-label="Interview recording" />
      <button className="btn btn-ghost btn-sm" type="button" onClick={download} disabled={downloading || !audioBlob}>
        {downloading ? 'Preparing…' : 'Download recording'}
      </button>
      {downloadError && (
        <span className="hint" role="alert">Could not download the recording. Please try again.</span>
      )}
    </div>
  )
}
